/**
 * NotificationRepository — in-app notifications. Rows are readable/updatable
 * only by their recipient (RLS); creation happens via DB fan-out triggers.
 */
import { requireSupabase } from '@/lib/supabase/client'
import { appNotificationFromRow, type AppNotificationRow } from '@/lib/supabase/mappers'
import type { NotificationRepository } from '../interfaces'
import { fail, requireUserId } from './helpers'

export const notificationRepository: NotificationRepository = {
  async listMine(limit = 50) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('recipient_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) fail(error)
    return ((data ?? []) as AppNotificationRow[]).map(appNotificationFromRow)
  },

  async unreadCount() {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { count, error } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_id', userId)
      .is('read_at', null)
    if (error) fail(error)
    return count ?? 0
  },

  async markRead(id) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('id', id)
      .eq('recipient_id', userId)
      .is('read_at', null)
    if (error) fail(error)
  },

  async markAllRead() {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { error } = await supabase
      .from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('recipient_id', userId)
      .is('read_at', null)
    if (error) fail(error)
  },
}
